import { useState } from 'react';
import { INV_TEXT_CONSENT, INV_PATIENT } from '../../invisalign/mockData';

export interface InvTextConsentViewProps {
  /** The practice line the texts come from. */
  practiceName?: string | undefined;
  /** The patient's first name, shown in the greeting. */
  firstName?: string | undefined;
}

/**
 * The patient-facing text-consent page (amendment 33 §2, INV11TextConsent).
 * Reached from the "Yes, text me too" button in email 1 — a Brain §30 action
 * page at {practice}.elva.ai/p/<code>, no date of birth. The checkbox starts
 * unticked; "Text me" stays disabled until it is ticked; then "You're all set".
 */
export function InvTextConsentView({
  practiceName = 'Alamo Heights Dental',
  firstName = INV_PATIENT.first,
}: InvTextConsentViewProps) {
  const [ticked, setTicked] = useState(false);
  const [done, setDone] = useState(false);
  const c = INV_TEXT_CONSENT;

  return (
    <div className="relative isolate min-h-screen bg-[var(--cream-0)] font-[var(--font-body)] text-[var(--ink-1)] antialiased">
      <div className="relative z-10 mx-auto max-w-[460px] px-[18px] pb-16 pt-10 md:pt-14">
        {/* Practice header */}
        <div className="mb-6 flex items-center gap-2">
          <div className="grid size-9 place-items-center rounded-[10px] bg-[var(--ai-3)] text-[13px] font-semibold text-white">
            AH
          </div>
          <div className="leading-tight">
            <div className="text-[14px] font-semibold text-[var(--ink-1)]">{practiceName}</div>
            <div className="text-[12px] text-[var(--ink-3)]">aligner line</div>
          </div>
        </div>

        <div className="overflow-hidden rounded-[var(--r-lg)] border border-[var(--cream-2)] bg-[var(--cream-1)] shadow-[var(--sh-1)]">
          <div className="h-1 bg-[var(--grad-spectrum)]" />
          <div className="p-6 md:p-7">
            {done ? (
              <div className="py-6 text-center">
                <div className="mx-auto mb-4 grid size-12 place-items-center rounded-full bg-[var(--success-tint)] text-[22px] text-[var(--success)]">
                  ✓
                </div>
                <h1 className="mb-1 text-[20px] font-semibold tracking-[-0.02em] text-[var(--ink-1)]">
                  {c.done}
                </h1>
                <p className="text-[14px] leading-[1.55] text-[var(--ink-2)]">
                  We’ll send you at most three texts about clear aligners over the next two months,
                  from {c.fromNumber}. Reply STOP anytime to stop them.
                </p>
              </div>
            ) : (
              <>
                <h1 className="mb-1 text-[21px] font-semibold tracking-[-0.02em] text-[var(--ink-1)]">
                  {c.title}
                </h1>
                <p className="mb-5 text-[14px] leading-[1.55] text-[var(--ink-2)]">
                  Hi {firstName} — a couple of details before we start.
                </p>

                <dl className="mb-5 space-y-3 rounded-[var(--r-md)] border border-[var(--cream-2)] bg-[var(--cream-0)] p-4">
                  <div className="flex items-center justify-between gap-4">
                    <dt className="text-[13px] text-[var(--ink-3)]">Texts come from</dt>
                    <dd className="font-[var(--font-mono)] text-[13px] tabular-nums text-[var(--ink-1)]">
                      {c.fromNumber}
                    </dd>
                  </div>
                  <div className="flex items-center justify-between gap-4">
                    <dt className="text-[13px] text-[var(--ink-3)]">Your mobile</dt>
                    <dd className="font-[var(--font-mono)] text-[13px] tabular-nums text-[var(--ink-1)]">
                      {c.patientMobile}
                    </dd>
                  </div>
                </dl>

                <label className="mb-6 flex cursor-pointer items-start gap-3 rounded-[var(--r-md)] border border-[var(--cream-2)] bg-[var(--cream-1)] p-4 transition-[border-color] hover:border-[var(--cream-shadow)]">
                  <input
                    type="checkbox"
                    checked={ticked}
                    onChange={(e) => { setTicked(e.target.checked); }}
                    className="mt-0.5 size-[18px] shrink-0 accent-[var(--success)]"
                  />
                  <span className="text-[13.5px] leading-[1.5] text-[var(--ink-1)]">{c.checkbox}</span>
                </label>

                <button
                  type="button"
                  disabled={!ticked}
                  onClick={() => { setDone(true); }}
                  className="inline-flex h-[52px] w-full cursor-pointer items-center justify-center rounded-[var(--r-md)] border-0 bg-[var(--success)] px-[22px] text-[15.5px] font-semibold text-white shadow-[0_8px_18px_-10px_color-mix(in_srgb,var(--success)_70%,transparent)] transition-[background,transform] duration-150 hover:bg-[color-mix(in_srgb,var(--success)_86%,#000)] active:translate-y-px disabled:cursor-default disabled:opacity-50 disabled:shadow-none"
                >
                  Text me
                </button>
              </>
            )}
          </div>
        </div>

        <p className="mt-4 text-center text-[12px] leading-[1.5] text-[var(--ink-3)]">
          Up to 3 messages. Standard message and data rates may apply. Reply STOP to stop,
          reply HELP for help. You don’t have to agree to get care.
        </p>
      </div>
    </div>
  );
}
